<template>
  <component
    :is="clickable ? 'button' : 'div'"
    :type="clickable ? 'button' : undefined"
    class="storage-pill"
    :class="[`storage-pill--${variant}`, { 'storage-pill--clickable': clickable }]"
    :aria-label="`${label}: ${displayed}`"
  >
    <template v-if="variant === 'inline'">
      <span class="storage-pill__label">{{ label }}</span>
      <span class="storage-pill__chip">
        <AppIcon name="star" :size="30" class="storage-pill__star" />
        <span class="storage-pill__count cb-num">{{ displayed }}</span>
      </span>
    </template>
    <template v-else>
      <AppIcon name="star" :size="34" class="storage-pill__star" />
      <span class="storage-pill__texts">
        <span class="storage-pill__label">{{ label }}</span>
        <span class="storage-pill__count cb-num">{{ displayed }}</span>
      </span>
    </template>
  </component>
</template>

<script setup>
/**
 * StoragePill — «Хранилище» со звездой и балансом.
 *
 * <StoragePill :count="session.stars" />                    // экраны 1, 9: карточка, звезда слева
 * <StoragePill :count="8" variant="glow" />                  // экран 10: лаймовая обводка + свечение
 * <StoragePill :count="48" variant="inline" animate />       // экран 8: «Хранилище | ★ 48», count-up
 *
 * props: count (number), variant ('card'|'glow'|'inline'), label ('Хранилище'),
 *        animate (плавно досчитать до нового значения), clickable (рендер <button>, слушайте @click).
 */
import { onBeforeUnmount, ref, watch } from "vue";
import AppIcon from "./AppIcon.vue";
import { prefersReducedMotion } from "@/utils/format";

const props = defineProps({
  count: { type: Number, default: 0 },
  variant: { type: String, default: "card", validator: (v) => ["card", "glow", "inline"].includes(v) },
  label: { type: String, default: "Хранилище" },
  animate: { type: Boolean, default: false },
  /** С какого значения начинать count-up при первом показе (по умолчанию — сразу count). */
  from: { type: Number, default: null },
  clickable: { type: Boolean, default: false },
});

const TWEEN_MS = 900;
/** Страховка: rAF не вызывается в фоне/скрытом WebView — итоговое значение ставим по таймеру. */
const TWEEN_FALLBACK_MS = TWEEN_MS + 150;

const displayed = ref(props.animate && props.from !== null ? props.from : props.count || 0);
let frame = 0;
let fallbackTimer = null;

function stopTween() {
  cancelAnimationFrame(frame);
  frame = 0;
  clearTimeout(fallbackTimer);
  fallbackTimer = null;
}

function tweenTo(target) {
  stopTween();
  const start = displayed.value;
  if (!props.animate || prefersReducedMotion() || start === target) {
    displayed.value = target;
    return;
  }
  const startedAt = performance.now();
  const step = (now) => {
    const t = Math.min(1, Math.max(0, (now - startedAt) / TWEEN_MS));
    const eased = 1 - Math.pow(1 - t, 3);
    displayed.value = Math.round(start + (target - start) * eased);
    if (t < 1) {
      frame = requestAnimationFrame(step);
    } else {
      stopTween();
    }
  };
  frame = requestAnimationFrame(step);
  fallbackTimer = setTimeout(() => {
    stopTween();
    displayed.value = target;
  }, TWEEN_FALLBACK_MS);
}

watch(
  () => props.count,
  (value) => tweenTo(Number(value) || 0),
  { immediate: props.animate && props.from !== null },
);

onBeforeUnmount(stopTween);
</script>

<style scoped>
.storage-pill {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  color: var(--cb-text);
  font-family: var(--cb-font);
  text-align: left;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 50%),
    var(--cb-surface-2);
  border: 1px solid var(--cb-border);
  box-shadow: var(--cb-inner-highlight), var(--cb-shadow-card);
}

.storage-pill--clickable {
  cursor: pointer;
}

.storage-pill--clickable:active {
  transform: scale(0.98);
}

.storage-pill__star {
  color: var(--cb-lime);
  filter: drop-shadow(0 0 8px rgba(221, 245, 90, 0.55));
}

.storage-pill__texts {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.storage-pill__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--cb-text);
  opacity: 0.85;
  white-space: nowrap;
}

.storage-pill__count {
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
}

/* card / glow (экраны 1, 9, 10) */
.storage-pill--card,
.storage-pill--glow {
  min-width: 132px;
  padding: 10px 18px 10px 14px;
  border-radius: var(--cb-radius-md);
}

.storage-pill--glow {
  border-color: var(--cb-lime-border);
  box-shadow: var(--cb-glow-sm), inset 0 0 14px rgba(221, 245, 90, 0.06);
}

/* inline (экран 8) */
.storage-pill--inline {
  gap: 0;
  padding: 5px 5px 5px 22px;
  border-radius: var(--cb-radius-pill);
}

.storage-pill--inline .storage-pill__label {
  padding-right: 18px;
  font-size: 15px;
  font-weight: 600;
  color: var(--cb-text-2);
  opacity: 1;
}

.storage-pill__chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 18px 8px 14px;
  border-radius: var(--cb-radius-pill);
  background: var(--cb-bg);
  border: 1px solid var(--cb-border);
}

.storage-pill--inline .storage-pill__count {
  font-size: 30px;
  color: var(--cb-lime);
  text-shadow: var(--cb-text-glow);
}
</style>
